/**
 * Hash-based routing logic for XLFlow.
 * All view detection and hash parsing is centralized here for testability and future migration to a router.
 */

export type View =
  | "dashboard"
  | "clients"
  | "ambassadors"
  | "pilots"
  | "operations"
  | "ops-landing"
  | "ops-dashboard"
  | "ops-my-work"
  | "ops-engagements"
  | "ops-engagement-detail"
  | "ops-process-case-detail"
  | "inventories"
  | "admin-users"
  | "admin-orgs"
  | "admin-products"
  | "admin-promotions"
  | "admin-processes"
  | "admin-billing"
  | "admin-email-templates"
  | "email-send"
  | "bookings"
  | "public-booking"
  | "tasks"
  | "profile"
  | "settings"
  | "set-password"
  | "reset-password"
  | "unsubscribe"
  | "survey-presentation-xlkey"
  | "arla-aerial-products"
  | "studio"
  | "studio-rule-sets"
  | "studio-rule-set-detail"
  | "studio-process-templates"
  | "studio-handlers"
  | "studio-publications"
  | "soumission"
  | "studio-process-template-editor";

/**
 * Maps the current hash (e.g. "#/clients", "#/ops/engagements/123") to a View.
 */
export function hashToView(hash: string): View {
  if (hash.startsWith("#/set-password")) return "set-password";
  if (hash.startsWith("#/reset-password")) return "reset-password";
  if (hash === "#/unsubscribe") return "unsubscribe";
  if (hash.startsWith("#/public-booking")) return "public-booking";
  if (hash.startsWith("#/survey-presentation-xlkey")) return "survey-presentation-xlkey";
  if (hash.startsWith("#/produits-aeriens")) return "arla-aerial-products";
  if (hash.startsWith("#/arla-produits-aeriens")) return "arla-aerial-products";
  if (hash === "#/profile") return "profile";
  if (hash === "#/settings") return "settings";
  if (hash === "#/clients" || hash.startsWith("#/clients?")) return "clients";
  if (hash === "#/ambassadors") return "ambassadors";
  if (hash === "#/pilots") return "pilots";
  if (hash === "#/operations") return "ops-landing";
  if (hash === "#/ops" || hash === "#/ops/") return "ops-landing";
  if (hash === "#/ops/dashboard") return "ops-dashboard";
  if (hash === "#/ops/my-work") return "ops-my-work";
  if (hash === "#/ops/engagements" || hash === "#/ops/engagements/") return "ops-engagements";
  if (hash.startsWith("#/ops/engagements/")) {
    const after = hash.slice("#/ops/engagements/".length);
    const parts = after.split("/").filter(Boolean);
    if (parts[1] === "process-cases" && parts[2]) return "ops-process-case-detail";
    if (parts[0] && parts[0] !== "process-cases") return "ops-engagement-detail";
  }
  if (hash.startsWith("#/ops/process-cases/")) return "ops-process-case-detail";
  if (hash === "#/inventories" || hash.startsWith("#/inventories/") || hash.startsWith("#/inventories?")) return "inventories";
  if (hash === "#/tasks") return "tasks";
  if (hash === "#/admin") return "admin-users";
  if (hash === "#/admin-orgs") return "admin-orgs";
  if (hash === "#/admin-products") return "admin-products";
  if (hash === "#/admin-promotions") return "admin-promotions";
  if (hash === "#/admin-processes") return "admin-processes";
  if (hash === "#/admin-billing") return "admin-billing";
  if (hash === "#/admin-email-templates") return "admin-email-templates";
  if (hash === "#/email-send") return "email-send";
  if (hash === "#/bookings") return "bookings";
  if (hash === "#/studio" || hash === "#/studio/") return "studio";
  if (hash === "#/studio/rule-sets" || hash.startsWith("#/studio/rule-sets?")) return "studio-rule-sets";
  if (hash.startsWith("#/studio/rule-sets/")) return "studio-rule-set-detail";
  if (hash === "#/studio/process-templates") return "studio-process-templates";
  if (hash.startsWith("#/studio/process-templates/") && (hash.includes("/editor") || hash.endsWith("/new"))) return "studio-process-template-editor";
  if (hash === "#/studio/handlers") return "studio-handlers";
  if (hash === "#/studio/publications") return "studio-publications";
  if (hash === "#/soumission" || hash.startsWith("#/soumission?")) return "soumission";
  return "dashboard";
}

/**
 * Returns the hash string for a given View (e.g. viewToHash("clients") => "#/clients").
 */
export function viewToHash(view: View): string {
  if (view === "profile") return "#/profile";
  if (view === "settings") return "#/settings";
  if (view === "clients") return "#/clients";
  if (view === "ambassadors") return "#/ambassadors";
  if (view === "pilots") return "#/pilots";
  if (view === "operations" || view === "ops-landing") return "#/ops";
  if (view === "ops-dashboard") return "#/ops/dashboard";
  if (view === "ops-my-work") return "#/ops/my-work";
  if (view === "ops-engagements") return "#/ops/engagements";
  if (view === "inventories") return "#/inventories";
  if (view === "tasks") return "#/tasks";
  if (view === "admin-users") return "#/admin";
  if (view === "admin-orgs") return "#/admin-orgs";
  if (view === "admin-products") return "#/admin-products";
  if (view === "admin-promotions") return "#/admin-promotions";
  if (view === "admin-processes") return "#/admin-processes";
  if (view === "admin-billing") return "#/admin-billing";
  if (view === "admin-email-templates") return "#/admin-email-templates";
  if (view === "email-send") return "#/email-send";
  if (view === "bookings") return "#/bookings";
  if (view === "public-booking") return "#/public-booking";
  if (view === "unsubscribe") return "#/unsubscribe";
  if (view === "arla-aerial-products") return "#/produits-aeriens";
  if (view === "studio") return "#/studio";
  if (view === "studio-rule-sets") return "#/studio/rule-sets";
  if (view === "studio-process-templates") return "#/studio/process-templates";
  if (view === "studio-process-template-editor") return "#/studio/process-templates/new/editor";
  if (view === "studio-handlers") return "#/studio/handlers";
  if (view === "studio-publications") return "#/studio/publications";
  if (view === "soumission") return "#/soumission";
  return "#/";
}

/** Hash to use when parsing; defaults to window.location.hash for callers that don't pass one. */
function getHash(hash?: string): string {
  return hash !== undefined ? hash : (typeof window !== "undefined" ? window.location.hash : "");
}

/**
 * Extract token from #/set-password?token=...
 */
const PUBLIC_BOOKING_PREFIX = "#/public-booking";

/**
 * Jeton du lien public de réservation (#/public-booking?t=...).
 */
export function getPublicBookingTokenFromHash(hash?: string): string {
  const h = getHash(hash);
  if (!h.startsWith(PUBLIC_BOOKING_PREFIX)) return "";
  const queryString = h.split("?", 2)[1] ?? "";
  return new URLSearchParams(queryString).get("t") ?? "";
}

export function getSetPasswordTokenFromHash(hash?: string): string {
  const h = getHash(hash);
  if (!h.startsWith("#/set-password")) return "";
  const queryString = h.split("?", 2)[1] ?? "";
  return new URLSearchParams(queryString).get("token") ?? "";
}

/**
 * Extract token from #/reset-password?token=...
 */
export function getResetPasswordTokenFromHash(hash?: string): string {
  const h = getHash(hash);
  if (!h.startsWith("#/reset-password")) return "";
  const queryString = h.split("?", 2)[1] ?? "";
  return new URLSearchParams(queryString).get("token") ?? "";
}

/** Extract engagement ID from #/ops/engagements/:id or #/ops/engagements/:eid/process-cases/:pid */
export function getOpsEngagementIdFromHash(hash?: string): string | null {
  const h = getHash(hash);
  const match = h.match(/#\/ops\/engagements\/([^/]+)/);
  return match ? match[1] : null;
}

/** Extract process case ID from #/ops/engagements/:eid/process-cases/:id or #/ops/process-cases/:id */
export function getOpsProcessCaseIdFromHash(hash?: string): string | null {
  const h = getHash(hash);
  const withEng = h.match(/#\/ops\/engagements\/[^/]+\/process-cases\/([^/]+)/);
  if (withEng) return withEng[1];
  const simple = h.match(/#\/ops\/process-cases\/([^/]+)/);
  return simple ? simple[1] : null;
}

/** Extract rule set ID from #/studio/rule-sets/:id */
export function getStudioRuleSetIdFromHash(hash?: string): string | null {
  const h = getHash(hash);
  const match = h.match(/#\/studio\/rule-sets\/([^/?#]+)/);
  return match ? match[1] : null;
}

/** Extract product_id query from hash (e.g. #/studio/rule-sets?product_id=...) */
export function getStudioProductIdFromHash(hash?: string): string | null {
  const h = getHash(hash);
  const idx = h.indexOf("?");
  if (idx === -1) return null;
  return new URLSearchParams(h.slice(idx + 1)).get("product_id");
}

/** Extract clientId, clientName and quoteId from #/soumission?clientId=...&clientName=...&quoteId=... (accepts lowercase keys too) */
export function getSoumissionQueryFromHash(
  hash?: string
): { clientId: string | null; clientName: string | null; quoteId: string | null } {
  const h = getHash(hash);
  const idx = h.indexOf("?");
  if (idx === -1) return { clientId: null, clientName: null, quoteId: null };
  const params = new URLSearchParams(h.slice(idx + 1));
  return {
    clientId: params.get("clientId") ?? params.get("clientid"),
    clientName: params.get("clientName") ?? params.get("clientname"),
    quoteId: params.get("quoteId") ?? params.get("quoteid"),
  };
}

/** Build hash for soumission view with optional quoteId (open existing) or clientId/clientName (new for client) */
export function buildSoumissionHash(options?: {
  quoteId?: string | null;
  clientId?: string | null;
  clientName?: string | null;
}): string {
  if (!options) return "#/soumission";
  const params = new URLSearchParams();
  if (options.quoteId) params.set("quoteId", options.quoteId);
  if (options.clientId) params.set("clientId", options.clientId);
  if (options.clientName) params.set("clientName", options.clientName);
  const q = params.toString();
  return q ? `#/soumission?${q}` : "#/soumission";
}

/** Extract selected client id from #/clients?selected=... (for back navigation from soumission). */
export function getClientsSelectedIdFromHash(hash?: string): string | null {
  const h = getHash(hash);
  if (!h.startsWith("#/clients")) return null;
  const idx = h.indexOf("?");
  if (idx === -1) return null;
  return new URLSearchParams(h.slice(idx + 1)).get("selected");
}

/** Build hash for clients view with optional pre-selected client (e.g. back from soumission). */
export function buildClientsHash(selectedClientId?: string | null): string {
  if (!selectedClientId) return "#/clients";
  return `#/clients?selected=${encodeURIComponent(selectedClientId)}`;
}

/** Extract template ID from #/studio/process-templates/:id/editor or .../new. Returns "new" or the UUID. */
export function getStudioProcessTemplateEditorIdFromHash(hash?: string): string {
  const h = getHash(hash);
  const match = h.match(/#\/studio\/process-templates\/([^/]+)(?:\/editor)?\/?$/);
  if (!match) return "new";
  return match[1] === "new" ? "new" : match[1];
}
